// utils/data/metaData.js

// Constants
import {
  // Basics
  CompanyName,
  CompanyPhoneNumber,
  FULL_BUSINESS_URL,
  LocationCountry,

  // Address
  AddressLine1,
  AddressLine2,
  AddressLine3,
  PostalCode,

  // Social + helpers
  SOCIAL_URLS,
  PageMediaHelper,

  // Business facts
  BUSINESS_SCHEMA_TYPE,
  BUSINESS_OPENING_HOURS,
  BUSINESS_PRICE_RANGE,
  BUSINESS_CURRENCY,
  BUSINESS_AREA_SERVED,
  BUSINESS_LANGUAGES,
  BUSINESS_SERVICE_TYPES,
  BUSINESS_GEO,

  // Graph links + booking
  ORG_ID,
  WEBSITE_ID,
  BOOKING_FORM_URL,

  // Tribute + links (keep — used in Links schema)
  APPLE_PAY_EMAIL,
  CASH_APP_URL,
  THRONE_URL,
  VENMO_URL,
} from '../constants/Constants';

/* -----------------------------------------
   OG/Twitter helper
----------------------------------------- */
const makeOgTwitter = ({ title, description, image, url }) => [
  { property: 'og:title', content: title },
  { property: 'og:description', content: description },
  { property: 'og:image', content: image },
  { property: 'og:url', content: url },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: title },
  { name: 'twitter:description', content: description },
  { name: 'twitter:image', content: image },
];

/* -----------------------------------------
   Geo helper
----------------------------------------- */
const buildGeo = () =>
  BUSINESS_GEO?.lat != null && BUSINESS_GEO?.lng != null
    ? {
        '@type': 'GeoCoordinates',
        latitude: BUSINESS_GEO.lat,
        longitude: BUSINESS_GEO.lng,
      }
    : undefined;

/* -----------------------------------------
   WebSite entity
----------------------------------------- */
export const websiteStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  url: FULL_BUSINESS_URL,
  name: CompanyName,
  publisher: {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: CompanyName,
    url: FULL_BUSINESS_URL,
    logo: { '@type': 'ImageObject', url: PageMediaHelper('logo') },
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: `${FULL_BUSINESS_URL}/?s={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

/* -----------------------------------------
   HOME
----------------------------------------- */
export const homePageStructuredData = {
  '@context': 'https://schema.org',
  '@type': BUSINESS_SCHEMA_TYPE,
  '@id': ORG_ID,
  name: CompanyName,
  url: FULL_BUSINESS_URL,
  description: `${CompanyName} provides professional services including ${BUSINESS_SERVICE_TYPES.join(
    ', '
  )}.`,
  image: PageMediaHelper('homePreview'),
  logo: PageMediaHelper('logo'),
  sameAs: SOCIAL_URLS,
  address: {
    '@type': 'PostalAddress',
    streetAddress: [AddressLine1, AddressLine2].filter(Boolean).join(', '),
    addressLocality: AddressLine3,
    postalCode: PostalCode,
    addressCountry: LocationCountry,
  },
  telephone: CompanyPhoneNumber,
  priceRange: BUSINESS_PRICE_RANGE,
  openingHours: BUSINESS_OPENING_HOURS,
  areaServed: BUSINESS_AREA_SERVED,
  availableLanguage: BUSINESS_LANGUAGES,
  geo: buildGeo(),
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const homePageAdditionalMeta = makeOgTwitter({
  title: `${CompanyName} | ${BUSINESS_SERVICE_TYPES[0]} & More`,
  description: `${CompanyName} — ${BUSINESS_SERVICE_TYPES.join(
    ', '
  )}. Submit your tribute and approach correctly.`,
  image: PageMediaHelper('homePreview'),
  url: FULL_BUSINESS_URL,
});


/* -----------------------------------------
   MAILING LIST (sign-up)
----------------------------------------- */
export const mailingListPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: `Join ${CompanyName}'s Mailing List`,
  url: `${FULL_BUSINESS_URL}/mailing-list`,
  description:
    `Subscribe to ${CompanyName}'s official mailing list for exclusive updates, session availability, tour announcements, and important notices.`,
  sameAs: SOCIAL_URLS,
  publisher: {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: CompanyName,
    url: FULL_BUSINESS_URL,
    logo: { '@type': 'ImageObject', url: PageMediaHelper('logo') },
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: CompanyPhoneNumber,
    contactType: 'Customer Service',
    areaServed: BUSINESS_AREA_SERVED,
    availableLanguage: BUSINESS_LANGUAGES,
  },
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const mailingListPageAdditionalMeta = makeOgTwitter({
  title: `Join ${CompanyName}'s Mailing List`,
  description:
    `Get direct updates from ${CompanyName}. Be the first to know about new sessions, tours, and exclusive announcements.`,
  image: PageMediaHelper('servicesPreview'),
  url: `${FULL_BUSINESS_URL}/mailing-list`,
});

/* -----------------------------------------
   MAILING LIST CONFIRM
----------------------------------------- */
export const mailingListValidationStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: `Confirm Your Email – ${CompanyName} Mailing List`,
  url: `${FULL_BUSINESS_URL}/mailing/confirm`,
  description:
    `Confirm your email to finish joining ${CompanyName}'s official mailing list. Receive session availability, tour announcements, and important updates.`,
  mainEntity: {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: CompanyName,
    url: FULL_BUSINESS_URL,
  },
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const mailingListValidationAdditionalMeta = makeOgTwitter({
  title: `Confirm Your Email – ${CompanyName} Mailing List`,
  description:
    `Verify your email to complete your subscription to ${CompanyName}'s mailing list. Get session openings, tour dates, and key announcements.`,
  image: PageMediaHelper('servicesPreview'),
  url: `${FULL_BUSINESS_URL}/mailing/confirm`,
});

/* -----------------------------------------
   MAILING LIST UNSUBSCRIBE
----------------------------------------- */
export const mailingListUnsubscribeStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: `Unsubscribe – ${CompanyName} Mailing List`,
  url: `${FULL_BUSINESS_URL}/mailing/unsubscribe`,
  description:
    `Confirm removal from ${CompanyName}'s official mailing list. Manage your email preferences or resubscribe at any time.`,
  mainEntity: {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: CompanyName,
    url: FULL_BUSINESS_URL,
  },
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const mailingListUnsubscribeAdditionalMeta = makeOgTwitter({
  title: `Unsubscribe – ${CompanyName} Mailing List`,
  description:
    `You’re unsubscribing from ${CompanyName}'s mailing list. Your request will be processed and you can resubscribe whenever you want.`,
  image: PageMediaHelper('servicesPreview'),
  url: `${FULL_BUSINESS_URL}/mailing/unsubscribe`,
});

/* -----------------------------------------
   FAQ
----------------------------------------- */
export const faqPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How do I properly approach you?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'All approaches must be respectful, intentional, and informed. Read the FAQ fully before making contact. Low-effort messages are ignored.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is tribute required before contacting you?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Yes. Tribute is required before discussing sessions, customs, or ongoing dynamics. It demonstrates seriousness and respect for My time.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you offer online and in-person sessions?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Yes. Both online and in-person sessions are available for approved applicants. Availability depends on screening and tribute.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where can I find answers about customs, clips, and services?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'All details regarding clips, customs, sessions, and expectations are outlined on this FAQ page and the relevant sections of the site.',
      },
    },
  ],
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const faqPageAdditionalMeta = makeOgTwitter({
  title: `FAQ – Serving ${CompanyName}`,
  description:
    `Read before contacting ${CompanyName}. Clear answers on tribute, sessions, customs, expectations, and how to approach correctly.`,
  image: PageMediaHelper('logo'),
  url: `${FULL_BUSINESS_URL}/faq`,
});


/* -----------------------------------------
   LINKS & PAY SITES
----------------------------------------- */
export const linksPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: `Exclusive Links & Tribute Options - ${CompanyName}`,
  url: `${FULL_BUSINESS_URL}/links`,
  description:
    `Discover all official ways to connect with ${CompanyName}, including premium content, tribute options, and private platforms.`,
  sameAs: [
    THRONE_URL,
    CASH_APP_URL,
    VENMO_URL,
    APPLE_PAY_EMAIL,
    ...SOCIAL_URLS,
  ],
  publisher: {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: CompanyName,
    url: FULL_BUSINESS_URL,
    logo: { '@type': 'ImageObject', url: PageMediaHelper('logo') },
  },
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const linksPageAdditionalMeta = makeOgTwitter({
  title: `Exclusive Links & Tribute Options - ${CompanyName}`,
  description:
    `Explore all official platforms where you can connect, indulge, and tribute to ${CompanyName}.`,
  image: PageMediaHelper('logo'),
  url: `${FULL_BUSINESS_URL}/links`,
});

/* -----------------------------------------
   GALLERY
----------------------------------------- */
export const galleryPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: `${CompanyName} Gallery`,
  url: `${FULL_BUSINESS_URL}/gallery`,
  description:
    `Immerse yourself in the captivating visuals of ${CompanyName}. A glimpse into power, elegance, and seduction.`,
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const galleryPageAdditionalMeta = makeOgTwitter({
  title: `${CompanyName} Gallery`,
  description:
    'Step into a visual journey of dominance, seduction, and power. Witness My world through the lens.',
  image: `${FULL_BUSINESS_URL}/gallery/cover.jpg`,
  url: `${FULL_BUSINESS_URL}/gallery`,
});

/* -----------------------------------------
   LOCKTOBER
----------------------------------------- */
export const locktoberPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Event',
  name: `Locktober Challenge – ${CompanyName}`,
  url: `${FULL_BUSINESS_URL}/locktober`,
  description:
    `Join ${CompanyName}'s official Locktober chastity challenge. Submit your pledge, follow the rules, and devote yourself fully throughout October.`,
  startDate: '2025-10-01',
  endDate: '2025-10-31',
  eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
  eventStatus: 'https://schema.org/EventScheduled',
  organizer: {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: CompanyName,
    url: FULL_BUSINESS_URL,
  },
  image: [
    `${FULL_BUSINESS_URL}/assets/images/locktober/locktober-cover.jpg`,
    `${FULL_BUSINESS_URL}/assets/images/locktober/locktober-chastity-1.webp`,
    `${FULL_BUSINESS_URL}/assets/images/locktober/locktober-chastity-2.webp`,
  ],
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const locktoberPageAdditionalMeta = makeOgTwitter({
  title: `Locktober Challenge – ${CompanyName}`,
  description:
    `Join the official Locktober challenge with ${CompanyName}. Submit, obey, and devote yourself for the entire month of October.`,
  image: `${FULL_BUSINESS_URL}/assets/images/locktober/locktober-cover.jpg`,
  url: `${FULL_BUSINESS_URL}/locktober`,
});

/* -----------------------------------------
   CLIPS
----------------------------------------- */
export const clipsPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: `${CompanyName} Clips`,
  url: `${FULL_BUSINESS_URL}/clips`,
  description:
    `Unlock exclusive content, private clips, and premium experiences from ${CompanyName}. Indulge in My world beyond the ordinary.`,
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const clipsPageAdditionalMeta = makeOgTwitter({
  title: `${CompanyName} Clips`,
  description:
    `Access premium content, private clips, and exclusive fansite experiences from ${CompanyName}. Indulge in My world beyond the ordinary.`,
  image: `${FULL_BUSINESS_URL}/clips/cover.jpg`,
  url: `${FULL_BUSINESS_URL}/clips`,
});

/* -----------------------------------------
   FANSITES
----------------------------------------- */
export const fansitesPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: `${CompanyName} Fansites`,
  url: `${FULL_BUSINESS_URL}/clips-fansites`,
  description:
    `Unlock exclusive content, private clips, and premium experiences from ${CompanyName}. Indulge in My world beyond the ordinary.`,
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const fansitesPageAdditionalMeta = makeOgTwitter({
  title: `${CompanyName} Fansites`,
  description:
    `Access premium content, private clips, and exclusive fansite experiences from ${CompanyName}. Indulge in My world beyond the ordinary.`,
  image: `${FULL_BUSINESS_URL}/clips-fansites/cover.jpg`,
  url: `${FULL_BUSINESS_URL}/clips-fansites`,
});

/* -----------------------------------------
   TESTIMONIALS
----------------------------------------- */
export const testimonialsPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: `Testimonials - ${CompanyName}`,
  url: `${FULL_BUSINESS_URL}/testimonials`,
  description:
    `Explore authentic testimonials from devoted admirers and submissives of ${CompanyName}. Discover their experiences and the unforgettable encounters that define true submission.`,
  mainEntity: {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: CompanyName,
    url: FULL_BUSINESS_URL,
    logo: {
      '@type': 'ImageObject',
      url: PageMediaHelper('logo'),
    },
  },
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const testimonialsPageAdditionalMeta = makeOgTwitter({
  title: `Testimonials - Devotion to ${CompanyName}`,
  description:
    `Read the words of those who have submitted to ${CompanyName}. Explore their experiences of discipline, indulgence, and absolute devotion.`,
  image: `${FULL_BUSINESS_URL}/brand/testimonials-preview.jpg`,
  url: `${FULL_BUSINESS_URL}/testimonials`,
});

/* -----------------------------------------
   CONTACT
----------------------------------------- */
export const contactPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: `${CompanyName} Contact`,
  url: `${FULL_BUSINESS_URL}/contact`,
  description:
    `Contact ${CompanyName} to request a session, ask a question, or submit a tribute correctly.`,
  mainEntity: {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: CompanyName,
    url: FULL_BUSINESS_URL,
    telephone: CompanyPhoneNumber,
    sameAs: SOCIAL_URLS,
  },
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const contactPageAdditionalMeta = makeOgTwitter({
  title: `Contact ${CompanyName}`,
  description:
    `Reach out to ${CompanyName} for session inquiries, tribute protocol, and availability.`,
  image: PageMediaHelper('contactPreview'),
  url: `${FULL_BUSINESS_URL}/contact`,
});

/* -----------------------------------------
   BIO / ABOUT
----------------------------------------- */
export const AboutPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: CompanyName,
  url: FULL_BUSINESS_URL,
  jobTitle: 'Professional Dominatrix',
  description:
    `${CompanyName} is a powerful and elite Dominatrix offering exclusive experiences for those who seek true submission.`,
  image: {
    '@type': 'ImageObject',
    url: PageMediaHelper('homePreview'),
  },
  knowsAbout: [
    'Female Domination',
    'BDSM',
    'Fetish Exploration',
    'Power Exchange',
    'Sadism & Masochism',
  ],
  sameAs: SOCIAL_URLS,
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const AboutPageAdditionalMeta = makeOgTwitter({
  title: `${CompanyName} – Divine Dominance & Control`,
  description:
    `Step into the world of ${CompanyName}, where power, seduction, and control reign supreme. Discover what true submission means.`,
  image: PageMediaHelper('homePreview'),
  url: `${FULL_BUSINESS_URL}/bio`,
});

/* -----------------------------------------
   SESSIONS
----------------------------------------- */
export const sessionsPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: `${CompanyName}'s Sessions & Tribute`,
  provider: {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: CompanyName,
    url: FULL_BUSINESS_URL,
    telephone: CompanyPhoneNumber,
    sameAs: SOCIAL_URLS,
  },
  serviceType: 'Elite Dominatrix Sessions',
  description:
    `Indulge in your deepest fantasies with ${CompanyName}. Explore power, control, and submission in a personalized session tailored to your desires.`,
  offers: {
    '@type': 'Offer',
    priceCurrency: BUSINESS_CURRENCY,
    price: 'Varies',
    url: `${FULL_BUSINESS_URL}/sessions`,
  },
  areaServed: BUSINESS_AREA_SERVED,
  availableLanguage: BUSINESS_LANGUAGES,
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const sessionsPageAdditionalMeta = makeOgTwitter({
  title: `Sessions & Tribute – ${CompanyName}`,
  description:
    `Step into ${CompanyName}'s domain and experience the thrill of submission. Explore elite domination sessions tailored to your desires.`,
  image: PageMediaHelper('servicesPreview'),
  url: `${FULL_BUSINESS_URL}/sessions`,
});

/* -----------------------------------------
   MAINTENANCE
----------------------------------------- */
export const maintenancePageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Maintenance Page',
  description:
    `The ${CompanyName} website is currently undergoing maintenance. We’ll be back soon!`,
  url: `${FULL_BUSINESS_URL}/maintenance`,
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const maintenancePageAdditionalMeta = makeOgTwitter({
  title: `We'll Be Back Soon - ${CompanyName}`,
  description:
    `The ${CompanyName} website is currently undergoing maintenance. We’ll be back shortly with exciting updates.`,
  image: PageMediaHelper('maintenancePreview'),
  url: `${FULL_BUSINESS_URL}/maintenance`,
});

/* -----------------------------------------
   TERMS & POLICIES
----------------------------------------- */
export const termsAndPoliciesStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Terms and Policies',
  description:
    `Explore the terms of service and privacy policies of ${CompanyName}.`,
  url: `${FULL_BUSINESS_URL}/terms-and-policies`,
  mainEntity: {
    '@type': 'FAQPage',
    name: 'Terms and Policies FAQ',
    description:
      `Frequently asked questions about terms of service, privacy policies, and compliance at ${CompanyName}.`,
  },
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const termsAndPoliciesAdditionalMeta = makeOgTwitter({
  title: `Terms and Policies - ${CompanyName}`,
  description:
    `Understand the terms of service, privacy policies, and compliance guidelines of ${CompanyName}.`,
  image: PageMediaHelper('termsPreview'),
  url: `${FULL_BUSINESS_URL}/terms-and-policies`,
});

/* -----------------------------------------
   ERROR / 404
----------------------------------------- */
export const errorPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '404 Error Page',
  description: `The requested page could not be found on ${CompanyName}.`,
  url: `${FULL_BUSINESS_URL}/404`,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${FULL_BUSINESS_URL}/?s={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
  isPartOf: {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: FULL_BUSINESS_URL,
    name: CompanyName,
  },
};

export const errorPageAdditionalMeta = makeOgTwitter({
  title: 'Page Not Found - 404',
  description:
    `Oops! The page you’re looking for isn’t here. Visit ${CompanyName} to find what you need.`,
  image: PageMediaHelper('errorPreview'),
  url: `${FULL_BUSINESS_URL}/404`,
});
